import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import {
  singaporeLotteryMultipliersSchema,
  type SingaporeLotteryMultipliersOverride,
} from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';

export interface SingaporeLotteryMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/** Basis-point scale: returnBps / BPS_SCALE = total-return multiplier. */
const BPS_SCALE = 10000;

/**
 * Singapore Lottery is a FLAT paytable with exactly two distinct payout tiers,
 * so the editor exposes two values rather than 22 identical rows:
 *  - STANDARD_KEYS — every Big/Small/Odd/Even cell across Sum/First/Last-2.
 *    Each is a true 50/50, so RTP = return × 0.5. Default 19000 bps (1.90×).
 *  - EXACT_KEYS — the ten exact-first-digit cells (0–9). Each is a 1/10 shot,
 *    so RTP = return × 0.1. Default 95000 bps (9.50×).
 * Both defaults sit at RTP 95.0% — a uniform 5.0% house edge across the board.
 * Mirrors DEFAULT_SGL_RETURN_BPS in @apx/game-singaporelottery. On save both
 * tiers expand into a complete `returns` record so the settle path is fully
 * specified.
 */
const STANDARD_KEYS = [
  'SGL_SUM_BIG', 'SGL_SUM_SMALL', 'SGL_SUM_ODD', 'SGL_SUM_EVEN',
  'SGL_FIRST_BIG', 'SGL_FIRST_SMALL', 'SGL_FIRST_ODD', 'SGL_FIRST_EVEN',
  'SGL_LAST2_BIG', 'SGL_LAST2_SMALL', 'SGL_LAST2_ODD', 'SGL_LAST2_EVEN',
] as const;
const EXACT_KEYS = [
  'SGL_FIRST_0', 'SGL_FIRST_1', 'SGL_FIRST_2', 'SGL_FIRST_3', 'SGL_FIRST_4',
  'SGL_FIRST_5', 'SGL_FIRST_6', 'SGL_FIRST_7', 'SGL_FIRST_8', 'SGL_FIRST_9',
] as const;

/** Baked defaults — kept in sync with packages/games/singaporelottery/src/constants.ts. */
const DEFAULT_STANDARD_BPS = 19000; // 1.90× on a 50/50 → RTP 95.0% (5.0% edge)
const DEFAULT_EXACT_BPS = 95000; //   9.50× on a 1/10   → RTP 95.0% (5.0% edge)

/** Win probability per tier — the flat paytable's fixed uniform digit draw. */
const STANDARD_WIN_PROB = 0.5;
const EXACT_WIN_PROB = 0.1;

function rtpColor(rtp: number): string {
  if (rtp > 1.0) return 'var(--accent-red)';
  if (rtp > 0.97) return 'var(--accent-amber)';
  return 'var(--accent-green)';
}

/** Human hint: "1.90× return · RTP 95.0%" for a tier's bps value + win prob. */
function tierHint(bps: number, winProb: number): string {
  const mult = bps / BPS_SCALE;
  const rtp = mult * winProb;
  const over = rtp > 1.0 ? ' — over 100%!' : '';
  return `<strong>${mult.toFixed(2)}×</strong> return · RTP <strong style="color:${rtpColor(rtp)}">${(rtp * 100).toFixed(1)}%</strong>${over}`;
}

/**
 * Singapore Lottery payout-multiplier editor. The paytable is flat: two tunable
 * total-return values (Big/Small/Odd/Even cells vs. exact-first-digit cells)
 * that expand into the full `returns` record on save. Values are total-return
 * basis points (19000 = 1.90× = RTP 95.0% on a 50/50). Changes ride the
 * two-admin approval flow. A live RTP hint is shown per tier from the fixed
 * uniform digit draw (no RNG-weight lever exists for this game).
 */
export async function renderSingaporeLotteryMultipliersPage(
  root: HTMLElement,
  deps: SingaporeLotteryMultipliersPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — Singapore Lottery</h1>
    <p class="page-sub">Flat paytable in total-return basis points: <code>19000 = 1.90× = RTP 95.0%</code> on a 50/50 cell. Every Big/Small/Odd/Even cell shares one value; every exact first digit (0–9) shares another. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Payout tiers</h2>
      <table>
        <thead><tr><th>Tier</th><th>Return (bps)</th><th>Live RTP</th><th></th></tr></thead>
        <tbody>
          <tr>
            <td><strong>Big / Small / Odd / Even</strong><br><span class="muted">12 cells · Sum, First, Last-2 · 50/50</span></td>
            <td><input id="sgl-standard" type="number" min="0" max="100000000" step="1" style="width:120px" /></td>
            <td class="muted" id="sgl-standard-hint">—</td>
            <td class="muted" data-default>default ${DEFAULT_STANDARD_BPS} bps</td>
          </tr>
          <tr>
            <td><strong>Exact first digit</strong><br><span class="muted">10 cells · digit 0–9 · 1/10</span></td>
            <td><input id="sgl-exact" type="number" min="0" max="100000000" step="1" style="width:120px" /></td>
            <td class="muted" id="sgl-exact-hint">—</td>
            <td class="muted" data-default>default ${DEFAULT_EXACT_BPS} bps</td>
          </tr>
        </tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="sgl-reset" class="ghost">Reset to defaults</button>
        <button id="sgl-save" class="primary">Propose change</button>
        <span id="sgl-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="sgl-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const stdEl = root.querySelector<HTMLInputElement>('#sgl-standard')!;
  const exactEl = root.querySelector<HTMLInputElement>('#sgl-exact')!;
  const stdHintEl = root.querySelector<HTMLElement>('#sgl-standard-hint')!;
  const exactHintEl = root.querySelector<HTMLElement>('#sgl-exact-hint')!;
  const msgEl = root.querySelector<HTMLElement>('#sgl-msg')!;

  function updateHints(): void {
    stdHintEl.innerHTML = tierHint(Number(stdEl.value) || 0, STANDARD_WIN_PROB);
    exactHintEl.innerHTML = tierHint(Number(exactEl.value) || 0, EXACT_WIN_PROB);
  }
  stdEl.addEventListener('input', updateHints);
  exactEl.addEventListener('input', updateHints);

  /** Expand the two tier values into a complete `returns` record. */
  function readForm(): SingaporeLotteryMultipliersOverride {
    const std = Math.round(Number(stdEl.value) || 0);
    const exact = Math.round(Number(exactEl.value) || 0);
    const returns: Record<string, number> = {};
    for (const k of STANDARD_KEYS) returns[k] = std;
    for (const k of EXACT_KEYS) returns[k] = exact;
    return { version: 1, returns } as SingaporeLotteryMultipliersOverride;
  }

  /** Seed the two tier inputs from a representative cell of each tier. */
  function fillForm(value: SingaporeLotteryMultipliersOverride | null): void {
    const returns = (value?.returns ?? {}) as Record<string, number>;
    stdEl.value = String(returns.SGL_SUM_BIG ?? DEFAULT_STANDARD_BPS);
    exactEl.value = String(returns.SGL_FIRST_0 ?? DEFAULT_EXACT_BPS);
    updateHints();
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
  fillForm(current as SingaporeLotteryMultipliersOverride | null);

  root.querySelector<HTMLButtonElement>('#sgl-reset')!.addEventListener('click', () => {
    stdEl.value = String(DEFAULT_STANDARD_BPS);
    exactEl.value = String(DEFAULT_EXACT_BPS);
    updateHints();
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#sgl-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = singaporeLotteryMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data as SingaporeLotteryMultipliersOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#sgl-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
          fillForm(fresh.value as SingaporeLotteryMultipliersOverride | null);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
